/**
 * Admin console — tenant management, theme preview, usage dashboard.
 *
 * Phase A: all client-side with localStorage themes.
 * Phase B: wired to /v1/tenants API.
 */

import { useState } from "react";
import { Palette, Settings, Users, BarChart3 } from "lucide-react";

import { useTheme } from "@/contexts/ThemeContext";
import {
  DEFAULT_THEME,
  EXAMPLE_CORPORATE_THEME,
  applyTheme,
  saveTheme,
  type TenantTheme,
} from "@/lib/theme";

type Tab = "tenants" | "theme" | "usage";

export default function Admin() {
  const [tab, setTab] = useState<Tab>("theme");

  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-8 pb-24">
      <h1 className="text-2xl font-bold text-slate-100 mb-6">Admin Console</h1>

      {/* Tab bar */}
      <div className="flex gap-2 mb-8 overflow-x-auto">
        <TabBtn icon={Palette} label="Theme" active={tab === "theme"} onClick={() => setTab("theme")} />
        <TabBtn icon={Users} label="Tenants" active={tab === "tenants"} onClick={() => setTab("tenants")} />
        <TabBtn icon={BarChart3} label="Usage" active={tab === "usage"} onClick={() => setTab("usage")} />
      </div>

      {tab === "theme" && <ThemeEditor />}
      {tab === "tenants" && <TenantList />}
      {tab === "usage" && <UsageDashboard />}
    </section>
  );
}

function TabBtn({
  icon: Icon,
  label,
  active,
  onClick,
}: {
  icon: typeof Settings;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
        active
          ? "bg-blue-500/15 text-blue-300 border border-blue-500/40"
          : "text-slate-400 hover:text-slate-200 border border-transparent hover:border-slate-700"
      }`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Theme editor
// ---------------------------------------------------------------------------
function ThemeEditor() {
  const currentTheme = useTheme();
  const presets = [
    { label: "Default (Analytica DSS)", theme: DEFAULT_THEME },
    { label: "Corporate Blue/Teal", theme: EXAMPLE_CORPORATE_THEME },
  ];

  function applyPreset(theme: TenantTheme) {
    applyTheme(theme);
    saveTheme(theme);
    // Force reload to pick up new ThemeProvider state
    window.location.reload();
  }

  return (
    <div className="space-y-6">
      <div className="ta-card p-6">
        <h2 className="text-base font-semibold text-slate-100 mb-4">
          Theme Presets
        </h2>
        <p className="text-sm text-slate-400 mb-4">
          Select a preset to instantly reskin the entire application.
          White-label buyers get their own config.
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          {presets.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => applyPreset(p.theme)}
              className="ta-card p-4 text-left hover:border-blue-500/40 transition group"
            >
              <div className="flex items-center gap-3 mb-3">
                <div
                  className="w-8 h-8 rounded-lg"
                  style={{
                    background: `linear-gradient(135deg, ${p.theme.accentBlue}, ${p.theme.accentPurple})`,
                  }}
                />
                <div>
                  <div className="text-sm font-semibold text-slate-100">
                    {p.theme.productName}
                  </div>
                  <div className="text-xs text-slate-500">{p.label}</div>
                </div>
              </div>
              <div className="flex gap-2">
                {[p.theme.accentBlue, p.theme.accentPurple, p.theme.accentPink].map(
                  (c) => (
                    <div
                      key={c}
                      className="w-6 h-6 rounded-full border border-slate-700"
                      style={{ backgroundColor: c }}
                    />
                  ),
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="ta-card p-6">
        <h2 className="text-base font-semibold text-slate-100 mb-4">
          Current Theme
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
          <ThemeField label="Product Name" value={currentTheme.productName} />
          <ThemeField label="Badge" value={currentTheme.badge} />
          <ThemeField label="Accent Blue" value={currentTheme.accentBlue} color />
          <ThemeField label="Accent Purple" value={currentTheme.accentPurple} color />
          <ThemeField label="Accent Pink" value={currentTheme.accentPink} color />
          <ThemeField label="Background" value={currentTheme.bg} color />
          <ThemeField
            label="Show Analytica Credit"
            value={currentTheme.showAnalyticaCredit ? "Yes" : "No"}
          />
        </div>
      </div>
    </div>
  );
}

function ThemeField({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      {color && (
        <div
          className="w-4 h-4 rounded-full border border-slate-700 shrink-0"
          style={{ backgroundColor: value }}
        />
      )}
      <div>
        <div className="text-[10px] uppercase tracking-wider text-slate-500">
          {label}
        </div>
        <div className="text-slate-200 font-mono text-xs truncate">
          {value}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tenant list
// ---------------------------------------------------------------------------
function TenantList() {
  const tenants = [
    {
      slug: "default",
      name: "TalentAlign Default",
      users: 1,
      matches: 0,
    },
  ];

  return (
    <div className="ta-card p-6">
      <h2 className="text-base font-semibold text-slate-100 mb-4">
        Tenants
      </h2>
      <p className="text-sm text-slate-400 mb-4">
        Each tenant gets isolated data, custom theming, and usage metering.
        Phase B connects to the /v1/tenants API.
      </p>
      <div className="rounded-lg border border-slate-800/60 overflow-hidden">
        <div className="grid grid-cols-[1fr_80px_80px] gap-2 px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500 bg-slate-900/50 border-b border-slate-800/60">
          <div>Tenant</div>
          <div className="text-right">Users</div>
          <div className="text-right">Matches</div>
        </div>
        {tenants.map((t) => (
          <div
            key={t.slug}
            className="grid grid-cols-[1fr_80px_80px] gap-2 px-3 py-3 border-b border-slate-800/40 last:border-b-0"
          >
            <div>
              <div className="text-sm text-slate-100">{t.name}</div>
              <div className="text-xs text-slate-500 font-mono">{t.slug}</div>
            </div>
            <div className="text-right text-sm text-slate-300">{t.users}</div>
            <div className="text-right text-sm text-slate-300">{t.matches}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Usage dashboard placeholder
// ---------------------------------------------------------------------------
function UsageDashboard() {
  return (
    <div className="ta-card p-6">
      <h2 className="text-base font-semibold text-slate-100 mb-4">
        Usage Dashboard
      </h2>
      <p className="text-sm text-slate-400 mb-4">
        Per-tenant usage metering: matches scored, LLM tokens consumed,
        and estimated costs. Phase B populates from the `usage` table.
      </p>
      <div className="grid sm:grid-cols-3 gap-4">
        <UsageCard label="Matches Today" value="0" />
        <UsageCard label="LLM Tokens" value="0" />
        <UsageCard label="Est. Cost (USD)" value="$0.00" />
      </div>
    </div>
  );
}

function UsageCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="ta-card p-4 text-center">
      <div className="text-2xl font-bold ta-gradient-text">{value}</div>
      <div className="text-xs text-slate-500 mt-1">{label}</div>
    </div>
  );
}
